/**
 * Jelölő címke — vészmegoldás, amikor a futár (pl. Spring) nem ad címkét, és
 * a csomagot az ő kérésükre jelölve adjuk le: feladó (BGFox Kft.), rendelésszám
 * vonalkóddal, címzett, tételek. 100×150 mm-es (4×6") címkenyomtatóra.
 */

export interface MarkerLabelData {
  orderRef: string;
  shop?: string;
  name: string;
  addressLines: string[];
  country?: string;
  phone?: string;
  email?: string;
  items: Array<{ quantity: number; text: string }>;
  packageType?: string | null;
  weightGrams?: number | null;
  note?: string;
}

// Code 128 minták (érték → vonal/köz szélességek), 106 = STOP.
const CODE128 = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
  '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
  '221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
  '212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
  '231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
  '314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
  '112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
  '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
  '114131', '311141', '411131', '211412', '211214', '211232', '2331112',
];

/** Code 128-B vonalkód SVG-ként (csak nyomtatható ASCII). */
export function code128Svg(text: string, height = 60): string {
  const clean = `${text || ''}`.replace(/[^\x20-\x7E]/g, '');
  const values = [104, ...clean.split('').map((ch) => ch.charCodeAt(0) - 32)];
  const checksum = values.reduce((sum, value, index) => sum + value * (index === 0 ? 1 : index), 0) % 103;
  const pattern = [...values, checksum, 106].map((value) => CODE128[value]).join('');
  const quiet = 10;
  let x = quiet;
  let bars = '';
  pattern.split('').forEach((width, index) => {
    const w = Number(width);
    if (index % 2 === 0) bars += `<rect x="${x}" y="0" width="${w}" height="${height}"/>`;
    x += w;
  });
  const total = x + quiet;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${total} ${height}" preserveAspectRatio="none" width="100%" height="${height}">${bars}</svg>`;
}

function esc(value: unknown): string {
  return `${value ?? ''}`
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function buildMarkerLabelHtml(data: MarkerLabelData): string {
  const items = data.items.slice(0, 6);
  const more = data.items.length - items.length;
  const meta = [
    data.packageType ? `Csomag: ${data.packageType}` : '',
    data.weightGrams ? `${data.weightGrams} g` : '',
    new Date().toLocaleDateString('hu-HU'),
  ].filter(Boolean).join(' · ');

  return `<!doctype html>
<html lang="hu"><head><meta charset="utf-8"><title>Jelölő címke ${esc(data.orderRef)}</title>
<style>
  @page { size: 100mm 150mm; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; color: #000; }
  body { font-family: Arial, Helvetica, sans-serif; }
  .label { width: 100mm; height: 150mm; padding: 5mm; display: flex; flex-direction: column; gap: 3mm; overflow: hidden; }
  .sender { border-bottom: 0.6mm solid #000; padding-bottom: 2mm; }
  .sender b { font-size: 7mm; letter-spacing: 0.3mm; display: block; }
  .sender span { font-size: 3mm; }
  .k { font-size: 2.8mm; font-weight: bold; text-transform: uppercase; letter-spacing: 0.2mm; }
  .order { border: 0.6mm solid #000; padding: 2mm; text-align: center; }
  .order .ref { font-size: 8mm; font-weight: bold; margin: 1mm 0; word-break: break-all; }
  .order .shop { font-size: 3mm; }
  .to { flex: 1; }
  .to .name { font-size: 5.5mm; font-weight: bold; margin: 1mm 0; }
  .to .line { font-size: 4.2mm; line-height: 1.3; }
  .to .country { font-size: 5.5mm; font-weight: bold; margin-top: 1mm; }
  .to .contact { font-size: 3.2mm; margin-top: 1mm; }
  .items { border-top: 0.4mm solid #000; padding-top: 1.5mm; font-size: 3mm; line-height: 1.35; }
  .items div { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .meta { font-size: 2.8mm; display: flex; justify-content: space-between; }
  @media screen { body { background: #ddd; } .label { background: #fff; margin: 10mm auto; box-shadow: 0 0 4mm rgba(0,0,0,.2); } }
</style></head>
<body><div class="label">
  <div class="sender"><span class="k">Feladó</span><b>BGFOX KFT.</b>${data.note ? `<span>${esc(data.note)}</span>` : ''}</div>
  <div class="order">
    <div class="k">Rendelésszám / Order ID</div>
    <div class="ref">${esc(data.orderRef)}</div>
    ${code128Svg(data.orderRef, 55)}
    ${data.shop ? `<div class="shop">${esc(data.shop)}</div>` : ''}
  </div>
  <div class="to">
    <div class="k">Címzett / Ship to</div>
    <div class="name">${esc(data.name)}</div>
    ${data.addressLines.filter(Boolean).map((line) => `<div class="line">${esc(line)}</div>`).join('')}
    ${data.country ? `<div class="country">${esc(data.country)}</div>` : ''}
    <div class="contact">${[data.phone, data.email].filter(Boolean).map(esc).join(' · ')}</div>
  </div>
  <div class="items">
    ${items.map((item) => `<div>${esc(item.quantity)} × ${esc(item.text)}</div>`).join('')}
    ${more > 0 ? `<div>+ ${more} további tétel</div>` : ''}
  </div>
  <div class="meta"><span>${esc(meta)}</span><span>BGFox ERP</span></div>
</div>
<script>window.addEventListener('load', function () { setTimeout(function () { window.print(); }, 250); });</script>
</body></html>`;
}

/** Új lapon megnyitja a címkét, és elindítja a nyomtatást. */
export function openMarkerLabel(data: MarkerLabelData): boolean {
  const win = window.open('', '_blank');
  if (!win) return false;
  win.document.open();
  win.document.write(buildMarkerLabelHtml(data));
  win.document.close();
  return true;
}
